// src/resources/dashboards/ProductEvolution/PortfolioView.tsx
//
// A carteira de redes. Não é relatório: é fila de trabalho. Cada linha responde
// "por que eu deveria cuidar desta rede hoje" — daí o motivo do risco aparecer
// em texto ao lado do nome, e não só um número de score.
//
// Paginação é REAL e visível. A tela antiga cortava em slice(0,10) sem dizer,
// e o gráfico de saúde chegava a mostrar as 10 redes MAIS saudáveis num painel
// cujo propósito é achar problema.
import React, { useState } from 'react';
import {
  Box, Chip, IconButton, Table, TableBody, TableCell, TableHead, TablePagination,
  TableRow, TableSortLabel, Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import {
  COLORS, DeltaChip, EmptyHint, ErrorCard, SkeletonCard, cardSx, fmtBrl, fmtNum, fmtPct,
} from '../shared';
// Type-only: `SectionState` não existe em runtime e o kit exporta valores no
// mesmo módulo — separar evita depender da elisão de import do bundler.
import type { SectionState } from '../shared';
import {
  ChainLifecycleStatus, ChurnReason, OrderDir, PortfolioChainRow, PortfolioData,
  PortfolioOrderBy, RISK_LABEL, RiskLevel, SIGNAL_LABEL, STATUS_LABEL,
} from './types';

const TABLE_HEIGHT = 520;

/** Preço por litro em centavos importa; fmtBrl arredonda pra real e some com a diferença. */
const fmtBrlPrecise = (v: number | null | undefined, decimals = 2) =>
  v === null || v === undefined
    ? '—'
    : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const RISK_COLOR: Record<RiskLevel, string> = {
  high: COLORS.danger,
  medium: COLORS.warning,
  low: COLORS.success,
};

const STATUS_COLOR: Record<ChainLifecycleStatus, string> = {
  cancelada: COLORS.faint,
  implantacao: COLORS.blue,
  parada: COLORS.warning,
  em_risco: COLORS.danger,
  ativa: COLORS.success,
};

const softBg = (hex: string) => `${hex}1F`; // 12% de opacidade, no mesmo tom do chip

const StatusChip = ({ status }: { status: ChainLifecycleStatus }) => {
  const color = STATUS_COLOR[status] ?? COLORS.faint;
  return (
    <Chip
      size="small"
      label={STATUS_LABEL[status] ?? status}
      sx={{ height: 20, fontSize: 10.5, fontWeight: 700, color, bgcolor: softBg(color), border: `1px solid ${color}44` }}
    />
  );
};

const ReasonList = ({ reasons }: { reasons: ChurnReason[] }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    {reasons.map((reason, i) => (
      <Typography key={`${reason.signal}-${i}`} sx={{ fontSize: 11.5, lineHeight: 1.5 }}>
        <b>{SIGNAL_LABEL[reason.signal] ?? reason.signal}</b> · {reason.text} <span style={{ opacity: 0.7 }}>(+{reason.weight})</span>
      </Typography>
    ))}
  </Box>
);

const RiskCell = ({ row }: { row: PortfolioChainRow }) => {
  const color = RISK_COLOR[row.riskLevel] ?? COLORS.faint;
  const chip = (
    <Chip
      size="small"
      label={`${RISK_LABEL[row.riskLevel] ?? row.riskLevel} · ${row.churnScore}`}
      sx={{ height: 24, fontSize: 11.5, fontWeight: 800, color: '#fff', bgcolor: color }}
    />
  );
  if (!row.reasons.length) return chip;
  return (
    <Tooltip
      arrow
      placement="left"
      title={<Box sx={{ p: 0.5 }}><ReasonList reasons={row.reasons} /></Box>}
      slotProps={{ tooltip: { sx: { maxWidth: 360, bgcolor: 'rgba(30,41,59,0.96)' } } }}
    >
      {chip}
    </Tooltip>
  );
};

const Th = ({ label, sortKey, orderBy, orderDir, onSort, align = 'right', hint }: {
  label: string;
  sortKey?: PortfolioOrderBy;
  orderBy: PortfolioOrderBy;
  orderDir: OrderDir;
  onSort: (key: PortfolioOrderBy) => void;
  align?: 'left' | 'right';
  hint?: string;
}) => {
  const content = sortKey ? (
    <TableSortLabel
      active={orderBy === sortKey}
      direction={orderBy === sortKey ? orderDir : 'desc'}
      onClick={() => onSort(sortKey)}
      sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: COLORS.muted }}
    >
      {label}
    </TableSortLabel>
  ) : (
    <Typography component="span" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: COLORS.muted }}>
      {label}
    </Typography>
  );
  return (
    <TableCell align={align} sx={{ borderBottom: '1px solid rgba(120,105,223,0.12)', whiteSpace: 'nowrap', py: 1 }}>
      {hint ? <Tooltip arrow title={hint}><span>{content}</span></Tooltip> : content}
    </TableCell>
  );
};

// `component="span"` de propósito: estas duas aparecem dentro do <span> que o
// Tooltip do MUI exige como filho, e <p> dentro de <span> é HTML inválido.
const Sub = ({ children }: { children: React.ReactNode }) => (
  <Typography component="span" sx={{ display: 'block', fontSize: 11, color: COLORS.faint, whiteSpace: 'nowrap' }}>{children}</Typography>
);

const Value = ({ children }: { children: React.ReactNode }) => (
  <Typography component="span" sx={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: COLORS.ink, whiteSpace: 'nowrap' }}>{children}</Typography>
);

interface PortfolioViewProps {
  state: SectionState<PortfolioData>;
  orderBy: PortfolioOrderBy;
  orderDir: OrderDir;
  onSort: (key: PortfolioOrderBy) => void;
  /** 0-based, como o TablePagination do MUI. */
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

const PortfolioView = ({
  state, orderBy, orderDir, onSort, page, perPage, onPageChange, onPerPageChange,
}: PortfolioViewProps) => {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Tríptico. Stale-while-revalidate: com `data` na mão, trocar filtro ou página
  // NÃO volta pro skeleton — a tabela antiga fica no lugar até a nova chegar.
  if (state.loading && !state.data) return <SkeletonCard height={TABLE_HEIGHT} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={TABLE_HEIGHT} />;
  if (!state.data) return null;

  const { chains, total, computing } = state.data;
  const firstIndex = total === 0 ? 0 : page * perPage + 1;
  const lastIndex = Math.min(total, page * perPage + chains.length);

  return (
    <Box sx={{ ...cardSx, p: 0, overflow: 'hidden' }}>
      <Box sx={{
        px: 2.25, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
        borderBottom: '1px solid rgba(120,105,223,0.10)',
      }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: COLORS.ink }}>Carteira de redes</Typography>
        {/* Contagem sempre visível: quem olha precisa saber que está vendo um recorte. */}
        <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
          {total > 0 ? `mostrando ${fmtNum(firstIndex)}–${fmtNum(lastIndex)} de ${fmtNum(total)} redes` : 'nenhuma rede no recorte atual'}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: COLORS.faint, ml: 'auto' }}>
          Clique na linha para abrir o detalhe da rede
        </Typography>
      </Box>

      {!chains.length ? (
        <EmptyHint text={computing
          ? 'Preparando a carteira pela primeira vez… os dados aparecem assim que o cálculo terminar.'
          : 'Nenhuma rede encontrada com os filtros atuais.'} />
      ) : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 36, borderBottom: '1px solid rgba(120,105,223,0.12)' }} />
                  <Th label="Rede" sortKey="name" align="left" orderBy={orderBy} orderDir={orderDir} onSort={onSort} />
                  <Th label="Risco" sortKey="churnScore" orderBy={orderBy} orderDir={orderDir} onSort={onSort} />
                  <Th label="Ticket médio" sortKey="avgTicket" orderBy={orderBy} orderDir={orderDir} onSort={onSort}
                    hint="Litros por abastecimento e preço por litro ao lado: sem eles, alta de preço do combustível se confunde com ganho de produto." />
                  <Th label="Abastecimentos" sortKey="fillins" orderBy={orderBy} orderDir={orderDir} onSort={onSort} />
                  <Th label="Clientes ativos" sortKey="activeCustomers" orderBy={orderBy} orderDir={orderDir} onSort={onSort} />
                  <Th label="Penetração" sortKey="appPenetration" orderBy={orderBy} orderDir={orderDir} onSort={onSort}
                    hint="Parcela da pista identificada pelo app. Sem dado de pista, aparece traço." />
                  <Th label="Postos" orderBy={orderBy} orderDir={orderDir} onSort={onSort} />
                  <Th label="MRR" sortKey="mrr" orderBy={orderBy} orderDir={orderDir} onSort={onSort} />
                  <Th label="Potencial de upsell" sortKey="upsellPotential" orderBy={orderBy} orderDir={orderDir} onSort={onSort}
                    hint="Potencial declarado — os preços de módulo não entram na cobrança real." />
                </TableRow>
              </TableHead>
              <TableBody>
                {chains.map(row => {
                  const expanded = expandedId === row.chainId;
                  const topReason = row.reasons[0];
                  return (
                    <React.Fragment key={row.chainId}>
                      <TableRow
                        hover
                        onClick={() => navigate(`/dashboard-evolution/${row.chainId}`)}
                        sx={{ cursor: 'pointer', '& td': { borderBottom: expanded ? 'none' : '1px solid rgba(148,163,184,0.16)' } }}
                      >
                        <TableCell sx={{ py: 1 }}>
                          <IconButton
                            size="small"
                            aria-label={expanded ? 'Recolher motivos' : 'Ver todos os motivos'}
                            onClick={e => { e.stopPropagation(); setExpandedId(expanded ? null : row.chainId); }}
                          >
                            {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>

                        <TableCell sx={{ py: 1, minWidth: 300 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: COLORS.ink }}>{row.name}</Typography>
                            <StatusChip status={row.status} />
                            <OpenInNewIcon sx={{ fontSize: 13, color: COLORS.faint }} />
                          </Box>
                          {/* O motivo principal em texto é o ponto da tela: score sozinho não faz ninguém agir. */}
                          <Typography sx={{ fontSize: 11.5, color: topReason ? COLORS.muted : COLORS.faint, mt: 0.25 }}>
                            {topReason ? topReason.text : 'Nenhum sinal de risco no período'}
                            {row.reasons.length > 1 ? ` · +${row.reasons.length - 1} ${row.reasons.length === 2 ? 'motivo' : 'motivos'}` : ''}
                          </Typography>
                        </TableCell>

                        <TableCell align="right" sx={{ py: 1 }}>
                          <RiskCell row={row} />
                        </TableCell>

                        <TableCell align="right" sx={{ py: 1 }}>
                          <Value>{fmtBrl(row.avgTicket)}</Value>
                          <DeltaChip deltaPct={row.avgTicketDelta} />
                          <Sub>
                            {fmtNum(row.litersPerFillin, 1)} L/ab · {fmtBrlPrecise(row.pricePerLiter, 2)}/L
                            {row.avgTicketQuintile !== null ? ` · Q${row.avgTicketQuintile}` : ''}
                          </Sub>
                        </TableCell>

                        <TableCell align="right" sx={{ py: 1 }}>
                          <Value>{fmtNum(row.fillins)}</Value>
                          <DeltaChip deltaPct={row.fillinsDelta} />
                          <Sub>{fmtBrl(row.revenue)}</Sub>
                        </TableCell>

                        <TableCell align="right" sx={{ py: 1 }}>
                          <Value>{fmtNum(row.activeCustomers)}</Value>
                          <DeltaChip deltaPct={row.activeCustomersDelta} />
                          <Sub>{fmtNum(row.newCustomers)} novos</Sub>
                        </TableCell>

                        <TableCell align="right" sx={{ py: 1 }}>
                          <Value>{row.appPenetration === null ? '—' : fmtPct(row.appPenetration, 1)}</Value>
                          {row.appPenetration === null && <Sub>sem dado de pista</Sub>}
                        </TableCell>

                        <TableCell align="right" sx={{ py: 1 }}>
                          <Value>{fmtNum(row.activePlaces)}</Value>
                          <Sub>
                            {row.stalledPlaces > 0
                              ? <Box component="span" sx={{ color: COLORS.danger, fontWeight: 700 }}>{fmtNum(row.stalledPlaces)} parados</Box>
                              : 'todos abastecendo'}
                          </Sub>
                        </TableCell>

                        <TableCell align="right" sx={{ py: 1 }}>
                          <Value>{fmtBrl(row.mrr)}</Value>
                          <Sub>
                            {row.overdueBills > 0
                              ? <Box component="span" sx={{ color: COLORS.danger, fontWeight: 700 }}>
                                {fmtNum(row.overdueBills)} vencida{row.overdueBills > 1 ? 's' : ''} · {fmtBrl(row.overdueValue)}
                              </Box>
                              : 'sem atraso'}
                          </Sub>
                        </TableCell>

                        <TableCell align="right" sx={{ py: 1 }}>
                          <Tooltip arrow title="Potencial declarado — os preços de módulo não entram na cobrança real.">
                            <span>
                              <Value>{fmtBrl(row.upsellPotential)}</Value>
                              <Sub>{row.activeModules}/{row.enabledModules} módulos em uso</Sub>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {expanded && (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ bgcolor: 'rgba(120,105,223,0.04)', py: 1.75, borderBottom: '1px solid rgba(148,163,184,0.16)' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' }, gap: 2 }}>
                              <Box>
                                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: COLORS.muted, mb: 0.75 }}>
                                  Por que esta rede está nesse risco
                                </Typography>
                                {row.reasons.length
                                  ? <ReasonList reasons={row.reasons} />
                                  : <Typography sx={{ fontSize: 11.5, color: COLORS.faint }}>Nenhum sinal de risco acionado no período.</Typography>}
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: COLORS.muted, mb: 0.75 }}>
                                  Contexto
                                </Typography>
                                <Typography sx={{ fontSize: 11.5, lineHeight: 1.7 }}>
                                  Base total: <b>{fmtNum(row.totalCustomers)}</b> clientes · Faturamento no período: <b>{fmtBrl(row.revenue)}</b>
                                </Typography>
                                <Typography sx={{ fontSize: 11.5, lineHeight: 1.7 }}>
                                  Módulos contratados sem uso:{' '}
                                  <b>{row.downgradeRisk.length ? row.downgradeRisk.join(', ') : 'nenhum'}</b>
                                </Typography>
                                {row.canceledAt && (
                                  <Typography sx={{ fontSize: 11.5, color: COLORS.danger, fontWeight: 700 }}>
                                    Cancelada em {new Date(row.canceledAt).toLocaleDateString('pt-BR')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => onPageChange(next)}
            rowsPerPage={perPage}
            onRowsPerPageChange={e => onPerPageChange(Number(e.target.value))}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Redes por página"
            labelDisplayedRows={({ from, to, count }) => `${fmtNum(from)}–${fmtNum(to)} de ${fmtNum(count)}`}
            sx={{ borderTop: '1px solid rgba(120,105,223,0.10)' }}
          />
        </>
      )}
    </Box>
  );
};

export default PortfolioView;
